import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Proje {
    projeID: number;
    projeAdi: string;
}

interface Daire {
    daireID: number;
    daireNo: string;
    takipNo: string;
    daireDurumu: string;
    listeFiyati?: number | null;
    netM2?: number | null;
    brutM2?: number | null;
    projeID: number;
    proje: { projeID: number; projeAdi: string };
    blok: { blokID: number; blokAdi: string };
    kat: { katID: number; katAdi: string };
    daireTipi: { daireTipID: number; tipAdi: string };
    cephe: { cepheID: number; cepheAdi: string };
}

// Satılmamış sayılan durumlar
const SATILAMAYANLAR = ['Satışta', 'Rezerve'];

// ─── Yardımcılar ─────────────────────────────────────────────────────────────

const fmt = (n: number | null | undefined) =>
    n == null ? '—' : n.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtTL = (n: number | null | undefined) =>
    n == null ? '—' : '₺' + n.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// ─── Stil Sabitleri ───────────────────────────────────────────────────────────

const TABLE: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 11,
};

const TH: React.CSSProperties = {
    padding: '6px 8px',
    border: '1px solid #555',
    background: '#e0e0e0',
    fontWeight: 700,
    fontSize: 11,
    color: '#000',
    textAlign: 'left',
};

const TH_R: React.CSSProperties = { ...TH, textAlign: 'right' };

const TD: React.CSSProperties = {
    padding: '5px 8px',
    border: '1px solid #ccc',
    fontSize: 11,
    color: '#111',
    verticalAlign: 'middle',
};

const TD_R: React.CSSProperties = { ...TD, textAlign: 'right' };

// ─── Ana Bileşen ─────────────────────────────────────────────────────────────

export default function EnvanterRaporu() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [projeler, setProjeler] = useState<Proje[]>([]);
    const [daireler, setDaireler] = useState<Daire[]>([]);
    const [secilenProjeID, setSecilenProjeID] = useState<string>('');

    const raporTarihi = new Date().toLocaleDateString('tr-TR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
    });

    // ── Veri Yükleme ──────────────────────────────────────────────────────────

    const fetchProjeler = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/Projeler');
            setProjeler(res.data);
        } catch {
            toast.error('Proje listesi yüklenemedi');
        }
    }, []);

    const fetchDaireler = useCallback(async () => {
        setLoading(true);
        try {
            const params: Record<string, string | boolean> = { aktifMi: true };
            if (secilenProjeID) params.projeID = secilenProjeID;
            const res = await axiosInstance.get('/api/Daireler', { params });
            setDaireler(res.data);
        } catch {
            toast.error('Daire verileri yüklenemedi');
        } finally {
            setLoading(false);
        }
    }, [secilenProjeID]);

    useEffect(() => { fetchProjeler(); }, [fetchProjeler]);
    useEffect(() => { fetchDaireler(); }, [fetchDaireler]);

    // ── Hesaplamalar ──────────────────────────────────────────────────────────

    const tumDaireler = daireler;
    const satilmayanlar = daireler.filter(d => SATILAMAYANLAR.includes(d.daireDurumu));
    const satilmislar   = daireler.filter(d => !SATILAMAYANLAR.includes(d.daireDurumu));

    const toplamStokDeger = satilmayanlar.reduce((s, d) => s + (d.listeFiyati ?? 0), 0);
    const toplamListeDeger = tumDaireler.reduce((s, d) => s + (d.listeFiyati ?? 0), 0);
    const satisOrani = tumDaireler.length > 0
        ? Math.round((satilmislar.length / tumDaireler.length) * 100)
        : 0;

    const toplamNetM2 = satilmayanlar.reduce((s, d) => s + (d.netM2 ?? 0), 0);
    const ortFiyat = satilmayanlar.length > 0 ? toplamStokDeger / satilmayanlar.length : 0;

    // Projeye göre gruplama (satılmayan)
    interface ProjeGrup {
        projeAdi: string;
        satirlar: Daire[];
        toplamDeger: number;
        toplamM2: number;
    }
    const projeGruplari = new Map<number, ProjeGrup>();
    satilmayanlar.forEach(d => {
        const pid = d.projeID;
        if (!projeGruplari.has(pid)) {
            projeGruplari.set(pid, {
                projeAdi: d.proje?.projeAdi || `Proje #${pid}`,
                satirlar: [],
                toplamDeger: 0,
                toplamM2: 0,
            });
        }
        const g = projeGruplari.get(pid)!;
        g.satirlar.push(d);
        g.toplamDeger += d.listeFiyati ?? 0;
        g.toplamM2 += d.netM2 ?? 0;
    });
    // Blok → Kat sırası
    projeGruplari.forEach(g => {
        g.satirlar.sort((a, b) => {
            const blokFark = (a.blok?.blokAdi || '').localeCompare(b.blok?.blokAdi || '');
            if (blokFark !== 0) return blokFark;
            return (a.kat?.katAdi || '').localeCompare(b.kat?.katAdi || '', 'tr', { numeric: true });
        });
    });
    const projeGrupArr = Array.from(projeGruplari.entries()).sort((a, b) =>
        a[1].projeAdi.localeCompare(b[1].projeAdi)
    );

    // Durum dağılımı (satılmayan)
    const durumMap = new Map<string, number>();
    satilmayanlar.forEach(d => {
        durumMap.set(d.daireDurumu, (durumMap.get(d.daireDurumu) ?? 0) + 1);
    });

    // Tip dağılımı (satılmayan)
    const tipMap = new Map<string, { adet: number; deger: number }>();
    satilmayanlar.forEach(d => {
        const tip = d.daireTipi?.tipAdi || 'Belirsiz';
        const cur = tipMap.get(tip) ?? { adet: 0, deger: 0 };
        tipMap.set(tip, { adet: cur.adet + 1, deger: cur.deger + (d.listeFiyati ?? 0) });
    });
    const tipArr = Array.from(tipMap.entries()).sort((a, b) => b[1].deger - a[1].deger);

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>

            {/* ── Action Bar (yazdırılmaz) ───────────────────────────── */}
            <div className="envanter-no-print" style={{
                background: '#fff', borderBottom: '1px solid #e5e7eb',
                padding: '10px 24px', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button
                        onClick={() => navigate(-1)}
                        style={{ background: 'none', border: '1px solid #d1d5db', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#374151' }}
                    >
                        ← Geri
                    </button>
                    <div>
                        <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Kalan Envanter ve Stok Değer Raporu</span>
                        <span style={{ marginLeft: 10, fontSize: 11, color: '#94a3b8' }}>Satılmamış Bağımsız Bölüm Analizi</span>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {/* Proje Filtresi */}
                    <select
                        value={secilenProjeID}
                        onChange={e => setSecilenProjeID(e.target.value)}
                        style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 10px', fontSize: 13, color: '#374151', cursor: 'pointer' }}
                    >
                        <option value="">Tüm Projeler</option>
                        {projeler.map(p => (
                            <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>
                        ))}
                    </select>
                    {loading && <span style={{ fontSize: 12, color: '#94a3b8' }}>Yükleniyor...</span>}
                    <button
                        onClick={fetchDaireler}
                        disabled={loading}
                        style={{ background: '#f8fafc', border: '1px solid #d1d5db', padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#374151' }}
                    >
                        ↻ Yenile
                    </button>
                    <button
                        onClick={() => window.print()}
                        style={{ background: '#1a2440', color: '#fff', border: 'none', padding: '5px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                    >
                        🖨 Yazdır / PDF
                    </button>
                </div>
            </div>

            {/* ── Ekran KPI Kartları (yazdırılmaz) ──────────────────── */}
            <div className="envanter-no-print" style={{
                maxWidth: 900, margin: '18px auto 0', padding: '0 20px',
                display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12,
            }}>
                {[
                    { label: 'Satılmamış Daire', value: satilmayanlar.length.toString(), sub: `Toplam ${tumDaireler.length} bölümden` },
                    { label: 'Stok Güncel Değeri', value: fmtTL(toplamStokDeger), sub: 'Liste fiyatı toplamı', color: '#1d4ed8' },
                    { label: 'Toplam Net m²', value: fmt(toplamNetM2) + ' m²', sub: 'Satılmayan stok' },
                    { label: 'Satış Oranı', value: `%${satisOrani}`, sub: `${satilmislar.length} adet satıldı`, color: satisOrani >= 70 ? '#15803d' : satisOrani >= 40 ? '#b45309' : '#b91c1c' },
                ].map(card => (
                    <div key={card.label} style={{
                        background: '#fff', borderRadius: 10, padding: '14px 16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                        borderTop: `3px solid ${card.color ?? '#1a2440'}`,
                    }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{card.label}</div>
                        <div style={{ fontSize: 17, fontWeight: 800, color: card.color ?? '#1a2440', lineHeight: 1.2 }}>{card.value}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>{card.sub}</div>
                    </div>
                ))}
            </div>

            {/* ============================================================
                YAZDIRILABILIR A4 ALAN
            ============================================================ */}
            <div
                id="envanter-print-area"
                style={{
                    maxWidth: 860, margin: '20px auto 48px', background: '#fff',
                    padding: '32px 40px', boxShadow: '0 1px 6px rgba(0,0,0,0.09)',
                    fontFamily: 'Arial, Helvetica, sans-serif',
                }}
            >
                {/* ── Başlık ───────────────────────────────────────────── */}
                <div style={{ borderBottom: '2.5px solid #000', paddingBottom: 12, marginBottom: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <div style={{ fontSize: 18, fontWeight: 900, color: '#000', textTransform: 'uppercase', letterSpacing: '-0.5px' }}>
                                Kalan Envanter ve Stok Değer Raporu
                            </div>
                            <div style={{ fontSize: 11, color: '#444', marginTop: 3 }}>
                                Satılmamış Bağımsız Bölüm Analizi
                                {secilenProjeID && projeler.find(p => String(p.projeID) === secilenProjeID)
                                    ? ` — ${projeler.find(p => String(p.projeID) === secilenProjeID)!.projeAdi}`
                                    : ' — Tüm Projeler'}
                            </div>
                        </div>
                        <div style={{ textAlign: 'right', fontSize: 11, color: '#555', lineHeight: 1.7 }}>
                            <div>Rapor Tarihi: <strong>{raporTarihi}</strong></div>
                            <div style={{ fontSize: 10, color: '#999' }}>YapiCRM</div>
                        </div>
                    </div>
                </div>

                {/* ── A. Özet Tablo ────────────────────────────────────── */}
                <div style={{ fontWeight: 800, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', borderLeft: '4px solid #000', paddingLeft: 8, marginBottom: 8 }}>
                    A. Genel Özet
                </div>
                <table style={{ ...TABLE, border: '2px solid #000', marginBottom: 18 }}>
                    <tbody>
                        <tr style={{ background: '#f0f0f0' }}>
                            <td style={{ ...TD, fontWeight: 700, width: '55%', borderColor: '#999', fontSize: 12, paddingTop: 10, paddingBottom: 10 }}>
                                Toplam Satılmamış Bağımsız Bölüm Adedi
                            </td>
                            <td style={{ ...TD_R, fontWeight: 900, fontSize: 16, borderColor: '#999', paddingTop: 10, paddingBottom: 10 }}>
                                {satilmayanlar.length} adet
                            </td>
                        </tr>
                        <tr>
                            <td style={{ ...TD, borderColor: '#999' }}>Kalan Stok Güncel Liste Değeri (Toplam)</td>
                            <td style={{ ...TD_R, fontWeight: 800, fontSize: 14, borderColor: '#999', color: '#000' }}>{fmtTL(toplamStokDeger)}</td>
                        </tr>
                        <tr style={{ background: '#fafafa' }}>
                            <td style={{ ...TD, borderColor: '#999' }}>Toplam Net Alan (Satılmamış)</td>
                            <td style={{ ...TD_R, fontWeight: 700, borderColor: '#999' }}>{fmt(toplamNetM2)} m²</td>
                        </tr>
                        <tr>
                            <td style={{ ...TD, borderColor: '#999' }}>Ortalama Liste Fiyatı (Satılmamış)</td>
                            <td style={{ ...TD_R, fontWeight: 700, borderColor: '#999' }}>{fmtTL(ortFiyat)}</td>
                        </tr>
                        <tr style={{ background: '#fafafa' }}>
                            <td style={{ ...TD, borderColor: '#999' }}>Toplam Bağımsız Bölüm (Proje Geneli)</td>
                            <td style={{ ...TD_R, fontWeight: 700, borderColor: '#999' }}>{tumDaireler.length} adet</td>
                        </tr>
                        <tr>
                            <td style={{ ...TD, borderColor: '#999' }}>Satılan / Sözleşmeli Bölüm</td>
                            <td style={{ ...TD_R, fontWeight: 700, borderColor: '#999' }}>{satilmislar.length} adet</td>
                        </tr>
                        <tr>
                            <td style={{ ...TD, borderColor: '#999' }}>Tüm Bölümlerin Toplam Liste Değeri</td>
                            <td style={{ ...TD_R, fontWeight: 700, borderColor: '#999' }}>{fmtTL(toplamListeDeger)}</td>
                        </tr>
                        <tr style={{ background: '#e8e8e8' }}>
                            <td style={{ ...TD, fontWeight: 700, borderColor: '#555', borderTop: '2px solid #555', fontSize: 12 }}>
                                Satış Oranı (Gerçekleşme)
                            </td>
                            <td style={{ ...TD_R, fontWeight: 900, fontSize: 14, borderColor: '#555', borderTop: '2px solid #555' }}>
                                %{satisOrani} &nbsp;
                                <span style={{ fontSize: 11, fontWeight: 400 }}>
                                    ({satilmislar.length}/{tumDaireler.length} bölüm)
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* ── Çıkarım Kutusu ───────────────────────────────────── */}
                <div style={{
                    border: '2px solid #000', padding: '10px 14px', marginBottom: 18,
                    background: '#f9f9f9', fontSize: 12,
                }}>
                    <strong>Değerlendirme:</strong>{' '}
                    Elimizde{' '}
                    <strong>{fmtTL(toplamStokDeger)}</strong> liste değerinde,{' '}
                    <strong>{satilmayanlar.length} adet</strong> satılabilir bağımsız bölüm bulunmaktadır.
                    {toplamNetM2 > 0 && (
                        <> Toplam <strong>{fmt(toplamNetM2)} m²</strong> net alana sahip bu stok,
                            ortalama <strong>{fmtTL(ortFiyat)}</strong> birim fiyatla değerlenmektedir.</>
                    )}
                    {' '}Proje genelinde satış oranı <strong>%{satisOrani}</strong>'dir.
                </div>

                {/* ── B. Tipe Göre Stok Özeti ──────────────────────────── */}
                {tipArr.length > 0 && (
                    <>
                        <div style={{ fontWeight: 800, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', borderLeft: '4px solid #000', paddingLeft: 8, marginBottom: 8, marginTop: 18 }}>
                            B. Daire Tipine Göre Stok Özeti
                        </div>
                        <table style={{ ...TABLE, marginBottom: 18 }}>
                            <thead>
                                <tr>
                                    <th style={TH}>Daire Tipi</th>
                                    <th style={TH_R}>Adet</th>
                                    <th style={TH_R}>Liste Değeri (Toplam)</th>
                                    <th style={TH_R}>Ort. Birim Fiyat</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tipArr.map(([tip, data], i) => (
                                    <tr key={tip} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                        <td style={{ ...TD, paddingLeft: 16 }}>{tip}</td>
                                        <td style={TD_R}>{data.adet} adet</td>
                                        <td style={{ ...TD_R, fontWeight: 700 }}>{fmtTL(data.deger)}</td>
                                        <td style={TD_R}>{fmtTL(data.adet > 0 ? data.deger / data.adet : 0)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ background: '#e8e8e8' }}>
                                    <td style={{ ...TD, fontWeight: 700, borderTop: '1.5px solid #555' }}>TOPLAM</td>
                                    <td style={{ ...TD_R, fontWeight: 700, borderTop: '1.5px solid #555' }}>{satilmayanlar.length} adet</td>
                                    <td style={{ ...TD_R, fontWeight: 800, borderTop: '1.5px solid #555' }}>{fmtTL(toplamStokDeger)}</td>
                                    <td style={{ ...TD_R, fontWeight: 700, borderTop: '1.5px solid #555' }}>{fmtTL(ortFiyat)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </>
                )}

                {/* ── C. Kalem Kalem Daire Listesi ─────────────────────── */}
                <div style={{ fontWeight: 800, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', borderLeft: '4px solid #000', paddingLeft: 8, marginBottom: 8, marginTop: 18 }}>
                    C. Kalan Stok — Detaylı Daire Listesi
                </div>

                {satilmayanlar.length === 0 ? (
                    <div style={{ padding: '16px', border: '1px solid #ccc', textAlign: 'center', color: '#888', fontStyle: 'italic', fontSize: 12 }}>
                        Satılmamış bağımsız bölüm bulunamadı.
                    </div>
                ) : (
                    projeGrupArr.map(([pid, grup]) => (
                        <div key={pid} style={{ marginBottom: 20 }}>
                            {/* Proje Başlık Satırı */}
                            {projeGrupArr.length > 1 && (
                                <div style={{
                                    background: '#1a2440', color: '#fff',
                                    padding: '6px 10px', fontSize: 12, fontWeight: 700,
                                    marginBottom: 0, borderBottom: 'none',
                                    display: 'flex', justifyContent: 'space-between',
                                }}>
                                    <span>📁 {grup.projeAdi}</span>
                                    <span>{grup.satirlar.length} adet &nbsp;|&nbsp; {fmtTL(grup.toplamDeger)}</span>
                                </div>
                            )}
                            <table style={TABLE}>
                                <thead>
                                    <tr>
                                        <th style={{ ...TH, width: 30 }}>#</th>
                                        <th style={TH}>Blok</th>
                                        <th style={TH}>Kat</th>
                                        <th style={TH}>Kapı No</th>
                                        <th style={TH}>Daire Tipi</th>
                                        <th style={TH_R}>Net m²</th>
                                        <th style={TH_R}>Brüt m²</th>
                                        <th style={TH}>Cephe</th>
                                        <th style={TH}>Durum</th>
                                        <th style={TH_R}>Liste Fiyatı</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {grup.satirlar.map((d, idx) => (
                                        <tr key={d.daireID} style={{ background: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                            <td style={{ ...TD, color: '#888', fontSize: 10 }}>{idx + 1}</td>
                                            <td style={TD}>{d.blok?.blokAdi || '—'}</td>
                                            <td style={TD}>{d.kat?.katAdi || '—'}</td>
                                            <td style={{ ...TD, fontWeight: 600 }}>{d.daireNo}</td>
                                            <td style={TD}>{d.daireTipi?.tipAdi || '—'}</td>
                                            <td style={TD_R}>{d.netM2 != null ? fmt(d.netM2) + ' m²' : '—'}</td>
                                            <td style={TD_R}>{d.brutM2 != null ? fmt(d.brutM2) + ' m²' : '—'}</td>
                                            <td style={{ ...TD, fontSize: 10 }}>{d.cephe?.cepheAdi || '—'}</td>
                                            <td style={{ ...TD, fontSize: 10 }}>
                                                <span style={{
                                                    padding: '1px 6px', borderRadius: 3,
                                                    background: d.daireDurumu === 'Satışta' ? '#fff' : '#f0f0f0',
                                                    border: `1px solid ${d.daireDurumu === 'Satışta' ? '#aaa' : '#ccc'}`,
                                                    fontWeight: d.daireDurumu === 'Rezerve' ? 700 : 400,
                                                    color: d.daireDurumu === 'Rezerve' ? '#7c3aed' : '#333',
                                                }}>
                                                    {d.daireDurumu}
                                                </span>
                                            </td>
                                            <td style={{ ...TD_R, fontWeight: 600 }}>
                                                {d.listeFiyati != null ? fmtTL(d.listeFiyati) : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr style={{ background: '#e8e8e8' }}>
                                        <td colSpan={5} style={{ ...TD, fontWeight: 700, borderTop: '1.5px solid #555' }}>
                                            PROJE TOPLAMI &nbsp;({grup.satirlar.length} bölüm)
                                        </td>
                                        <td style={{ ...TD_R, fontWeight: 700, borderTop: '1.5px solid #555' }}>
                                            {fmt(grup.toplamM2)} m²
                                        </td>
                                        <td colSpan={3} style={{ ...TD, borderTop: '1.5px solid #555' }}>&nbsp;</td>
                                        <td style={{ ...TD_R, fontWeight: 800, fontSize: 12, borderTop: '1.5px solid #555' }}>
                                            {fmtTL(grup.toplamDeger)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    ))
                )}

                {/* ── Genel Toplam (çoklu proje) ───────────────────────── */}
                {projeGrupArr.length > 1 && satilmayanlar.length > 0 && (
                    <table style={{ ...TABLE, border: '2px solid #000', marginTop: 8, marginBottom: 18 }}>
                        <tbody>
                            <tr style={{ background: '#1a2440' }}>
                                <td style={{ ...TD, color: '#fff', fontWeight: 800, fontSize: 13, border: '1px solid #243259' }}>
                                    GENEL TOPLAM — TÜM PROJELER ({satilmayanlar.length} Satılmamış Bölüm)
                                </td>
                                <td style={{ ...TD_R, color: '#fff', fontWeight: 900, fontSize: 15, border: '1px solid #243259' }}>
                                    {fmtTL(toplamStokDeger)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                )}

                {/* ── Notlar / Açıklamalar ─────────────────────────────── */}
                <div style={{ marginTop: 20, borderTop: '1px solid #bbb', paddingTop: 10, fontSize: 10, color: '#555' }}>
                    <strong>Notlar:</strong>
                    <ul style={{ margin: '4px 0 0 0', paddingLeft: 18, lineHeight: 1.8 }}>
                        <li>"Satışta" ve "Rezerve" durumundaki bölümler stok envantere dahil edilmiştir.</li>
                        <li>"Sözleşme", "ArsaSahibi" ve "Taşeron" durumundaki bölümler stok dışı tutulmuştur.</li>
                        <li>Liste fiyatları girilmemiş bölümler değer hesabına dahil edilmemiştir.</li>
                    </ul>
                </div>

                {/* ── Altbilgi ─────────────────────────────────────────── */}
                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#999', borderTop: '1px solid #ccc', paddingTop: 6 }}>
                    <span>YapiCRM — Kalan Envanter ve Stok Değer Raporu</span>
                    <span>Oluşturulma: {new Date().toLocaleString('tr-TR')}</span>
                </div>
            </div>

            {/* ── Baskı Stilleri ───────────────────────────────────────── */}
            <style>{`
                @media print {
                    .envanter-no-print { display: none !important; }
                    body { background: #fff !important; margin: 0 !important; }
                    #envanter-print-area {
                        max-width: 100% !important;
                        margin: 0 !important;
                        padding: 10mm 12mm !important;
                        box-shadow: none !important;
                        font-size: 10px !important;
                    }
                    table { page-break-inside: auto; border-color: #555 !important; }
                    tr    { page-break-inside: avoid; page-break-after: auto; }
                    thead { display: table-header-group; }
                    tfoot { display: table-footer-group; }
                    @page { size: A4 portrait; margin: 8mm; }
                }
            `}</style>
        </div>
    );
}
